import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthResponse } from '../types/auth';
import { authService } from '../services/authService';

interface AuthContextType {
    user: User | null;
    login: (Email: string, Sifre: string) => Promise<void>;
    logout: () => void;
    loading: boolean;
    hasPermission: (modul: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    const updateUserState = (userData: AuthResponse | null) => {
        if (userData) {
            const userState: User = {
                kullaniciID: userData.kullaniciID,
                adSoyad: userData.adSoyad,
                email: userData.email,
                kullaniciRolu: userData.kullaniciRolu,
                yetkiler: userData.yetkiler ?? [],
                erisilebilirProjeler: userData.erisilebilirProjeler ?? []
            };
            setUser(userState);
        } else {
            setUser(null);
        }
    };

    useEffect(() => {
        const initializeAuth = () => {
            try {
                const userData = authService.getCurrentUser();
                console.log('Initializing auth with user data:', userData);
                updateUserState(userData);
            } catch (error) {
                console.error('Error initializing auth:', error);
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        initializeAuth();
    }, []);

    const login = async (Email: string, Sifre: string) => {
        try {
            const response = await authService.login({ Email, Sifre });
            localStorage.setItem('user', JSON.stringify(response));
            updateUserState(response);
        } catch (error) {
            throw error;
        }
    };

    const logout = () => {
        authService.logout();
        updateUserState(null);
    };

    const hasPermission = (modul: string): boolean => {
        if (!user) return false;
        if (user.kullaniciRolu === 'Admin') return true;
        return user.yetkiler?.includes(modul) ?? false;
    };

    const contextValue = {
        user,
        login,
        logout,
        loading,
        hasPermission
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};